import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { navigateToNewProject } from '../shared/navigation/navigateToNewProject'
import { C } from './landing.theme'
import { G } from './landing.styles'
import { Ic } from './ui/Ic'
import { useIsPhone } from './ui/useIsPhone'

import Nav      from './sections/Nav'
import Hero     from './sections/Hero'
import Features from './sections/Features'
import Steps    from './sections/Steps'
import Gallery  from './sections/Gallery'
import About    from './sections/About'
import Brands   from './sections/Brands'
import Reviews  from './sections/Reviews'
import Faq      from './sections/Faq'
import CtaBand  from './sections/CtaBand'
import Footer   from './sections/Footer'

/* ── Wave divider — placed at bottom of upper section, extends 2px into lower section ── */
function Waved({ children, to, flip = false, h = 52, first = false }) {
  const path = flip
    ? `M0,0 C360,${h} 1080,${h} 1440,0 L1440,${h} L0,${h} Z`
    : `M0,${h} C360,0 1080,0 1440,${h} L1440,${h} L0,${h} Z`
  return (
    <div style={{ position: 'relative' }}>
      {children}
      <div style={{ position: 'absolute', bottom: -2, left: 0, right: 0, lineHeight: 0, zIndex: 1, pointerEvents: 'none' }} className={first ? 'wave-first' : undefined}>
        <svg viewBox={`0 0 1440 ${h}`} preserveAspectRatio="none"
          style={{ display: 'block', width: '100%', height: h + 2 }} aria-hidden="true">
          <path d={path} fill={to}/>
        </svg>
      </div>
    </div>
  )
}


/* ── Sticky WhatsApp + Phone CTA ── */
function StickyCTA() {
  const [show,   setShow]   = useState(false)
  const [numPop, setNumPop] = useState(false)
  const isPhone = useIsPhone()
  const timerRef = useRef(null)

  useEffect(() => {
    const hero = document.getElementById('top')
    if (!hero) return
    const ob = new IntersectionObserver(([e]) => setShow(!e.isIntersecting), { threshold: 0.05 })
    ob.observe(hero)
    return () => ob.disconnect()
  }, [])

  /* Close popup when clicking outside */
  useEffect(() => {
    if (!numPop) return
    const close = e => {
      if (!e.target.closest('#sticky-phone-wrap')) setNumPop(false)
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [numPop])

  /* Auto-close popup after 5 s */
  const openNumPop = () => {
    setNumPop(true)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setNumPop(false), 5000)
  }

  const btnBase = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 42, height: 42, borderRadius: 999, cursor: 'pointer',
    border: '1px solid rgba(31,200,214,.25)',
    background: 'rgba(31,200,214,.15)',
    color: '#1FC8D6', transition: 'transform .2s, background .2s',
  }

  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 500,
      transition: 'transform .45s cubic-bezier(.34,1.56,.64,1), opacity .3s',
      transform: show ? 'translateY(0) scale(1)' : 'translateY(90px) scale(.88)',
      opacity: show ? 1 : 0,
      pointerEvents: show ? 'auto' : 'none',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(1,14,26,.82)', backdropFilter: 'blur(12px)',
        borderRadius: 999, padding: '6px 8px',
        border: '1px solid rgba(255,255,255,.08)',
        boxShadow: '0 8px 28px rgba(0,0,0,.45)',
      }}>

        {/* WhatsApp */}
        <a href="https://wa.me/77772296225" target="_blank" rel="noopener noreferrer"
          aria-label="WhatsApp"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 42, height: 42, borderRadius: 999, color: '#fff',
            textDecoration: 'none',
            background: 'linear-gradient(135deg,#25D366,#128C7E)',
            boxShadow: '0 4px 14px rgba(37,211,102,.4)',
            transition: 'transform .2s, box-shadow .2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(37,211,102,.55)' }}
          onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.boxShadow='0 4px 14px rgba(37,211,102,.4)' }}>
          <Ic n="wa" s={20}/>
        </a>

        {/* Phone — call on mobile, show number popup on desktop/tablet */}
        {isPhone ? (
          <a href="tel:+77772296225" aria-label="Позвонить" style={{ ...btnBase, textDecoration:'none' }}
            onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.background='rgba(31,200,214,.28)' }}
            onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.background='rgba(31,200,214,.15)' }}>
            <Ic n="phone" s={18}/>
          </a>
        ) : (
          <div id="sticky-phone-wrap" style={{ position:'relative' }}>
            {numPop && (
              <div style={{
                position:'absolute', bottom:'calc(100% + 10px)', right:0,
                background:'rgba(1,12,24,.96)', backdropFilter:'blur(14px)',
                border:'1px solid rgba(31,200,214,.28)', borderRadius:14,
                padding:'10px 16px 10px 14px', whiteSpace:'nowrap',
                boxShadow:'0 6px 28px rgba(0,0,0,.55)',
                animation:'wl-fade-in .18s ease',
              }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,.4)', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', textTransform:'uppercase', marginBottom:4 }}>Телефон</div>
                <div style={{ fontSize:15, fontWeight:700, color:'#fff', letterSpacing:'.01em' }}>+7 (777) 229-62-25</div>
              </div>
            )}
            <button onClick={openNumPop} aria-label="Показать номер телефона"
              style={{ ...btnBase, textDecoration:'none' }}
              onMouseEnter={e => { e.currentTarget.style.transform='scale(1.1)'; e.currentTarget.style.background='rgba(31,200,214,.28)' }}
              onMouseLeave={e => { e.currentTarget.style.transform='scale(1)'; e.currentTarget.style.background='rgba(31,200,214,.15)' }}>
              <Ic n="phone" s={18}/>
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

export default function LandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const goAuth = () => navigate(isAuthenticated ? '/dashboard' : '/auth')
  const goApp  = () => navigateToNewProject(navigate)

  const dark1  = C.bg1
  const dark2  = C.bg2
  const light  = C.paper
  const light2 = C.paper2

  return (
    <>
      <style>{G}</style>
      <StickyCTA/>

      <div style={{ background: C.bg0, color: C.s1, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", WebkitFontSmoothing: 'antialiased' }}>
        <Nav onLogin={goAuth} isAuth={isAuthenticated}/>
        <main>

          <Waved to={light} flip={false} h={110} first>
            <Hero/>
          </Waved>

          <Waved to={dark2} flip={true}>
            <Features/>
          </Waved>

          <Waved to={light} flip={false}>
            <Steps/>
          </Waved>

          <Waved to={dark2} flip={true}>
            <Gallery/>
          </Waved>

          <About/>

          <Waved to={light2} flip={false}>
            <Brands/>
          </Waved>

          <Reviews/>

          <Waved to={dark1} flip={true}>
            <Faq/>
          </Waved>

          <CtaBand onLogin={goAuth} isAuthenticated={isAuthenticated} onGoApp={goApp}/>

        </main>
        <Footer/>
      </div>
    </>
  )
}
